import { Button, CheckboxListItem, InputCurrency } from "@sellar-io/components";
import { OrderGetResponseType } from "@sellar-io/api-v2/types";
import { Fragment, ReactElement, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { SpinnerLegacy } from "../../../../components";
import { Alert } from "../../../../components/alert/Alert";
import { ReactHookFormSwitch } from "../../../../components/form/ReactHookFormSwitch";
import { InputLabel, InputContainer } from "@sellar-io/components";
import { InputText } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../../components/modal/Dialog";
import { apiV2AssertError } from "../../../../hooks/api/v2/apiV2Request";
import { useRefundCreate } from "../../../../hooks/api/v2/refund/useRefundCreate";
import { formatPriceInputToApi } from "../../../../hooks/usePriceFormat";
import { calculateLineItemTotal, formatCurrency } from "@sellar-io/common";

interface PartialPaymentRefundFormType {
  refundAmount: number;
  includeVat: boolean;
  notes: string | null;
}
export function CheckoutOrderPartialRefundDialog({
  active,
  orderId,
  orderItems,
  onDismiss,
  buyerName,
  vatPercentage,
  currency,
}: {
  orderId: number;
  active: boolean;
  orderItems: OrderGetResponseType["OrderItems"];
  onDismiss: () => void;
  buyerName: string;
  vatPercentage: number;
  currency: OrderGetResponseType["currency"];
}): ReactElement {
  const [selectedIds, setSelectedIds] = useState<string[]>();
  const [errorMessage, setErrorMessage] = useState<string>();
  const form = useForm({
    shouldUseNativeValidation: true,
    defaultValues: {
      refundAmount: 0,
      includeVat: true,
      notes: null,
    },
  });

  const refundCreateMutation = useRefundCreate();

  const refundAmount = useWatch({
    control: form.control,
    name: "refundAmount",
  });
  const includeVat = useWatch({
    control: form.control,
    name: "includeVat",
  });

  const getRefundTotal = (amount: number, includesVat: boolean) => {
    const subtotal = formatPriceInputToApi(amount) ?? 0;
    return includesVat
      ? calculateLineItemTotal({ price: subtotal, quantity: 1 }, vatPercentage)
      : subtotal;
  };
  const onSubmit = (data: PartialPaymentRefundFormType) => {
    setErrorMessage(undefined);
    const apiValue = getRefundTotal(data.refundAmount, data.includeVat);

    if (!apiValue || apiValue <= 0)
      return setErrorMessage("Refund amount must be greater than 0");
    refundCreateMutation.mutate(
      {
        data: {
          orderId,
          amount: apiValue,
          notes: data.notes,
        },
        idempotencyKey: crypto.randomUUID(),
      },
      {
        onSuccess: () => onDismiss(),
        onError: (error) =>
          setErrorMessage(
            apiV2AssertError(error)?.response?.data.message ?? "Unknown error",
          ),
      },
    );
  };

  const addOrderItemAndIncreaseRefundAmount = (
    orderItemPrice: number,
    productId: string,
  ) => {
    // add product id to list of selectedIds
    setSelectedIds([...(selectedIds ?? []), productId]);
    // set refund amount as existing refund amount + th order item amount
    form.setValue("refundAmount", refundAmount + orderItemPrice / 100);
  };

  const removeOrderItemAndDecreaseRefundAmount = (
    orderItemPrice: number,
    productId: string,
  ) => {
    // remove product id
    setSelectedIds(
      selectedIds?.filter((selectedGroup) => selectedGroup !== productId),
    );
    // get new refund amount
    const deductedRefundAmount = refundAmount - orderItemPrice / 100;
    form.setValue(
      "refundAmount",
      // if the user has decreased the amount manually & then removed the order item
      // the value might be < 0 so this handles that
      deductedRefundAmount < 0 ? 0 : deductedRefundAmount,
    );
  };
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon={"CHEVRON_DOWN"}
        onClickLeftIcon={onDismiss}
        title={"Issue partial refund"}
        description={`Select the order item(s) you would like to issue a refund for, or enter a custom refund amount`}
      />
      <DialogContent>
        {errorMessage ? (
          <Alert className={"w-full"} type={"error"} message={errorMessage} />
        ) : null}
        {refundCreateMutation.isPending ? (
          <SpinnerLegacy />
        ) : (
          <>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className={"flex flex-col gap-2"}
            >
              <InputLabel>Select order item(s)</InputLabel>
              <div className={"divide-y rounded-lg border border-solid"}>
                {orderItems.map((orderItem, i) => (
                  <Fragment key={i}>
                    {[...Array(orderItem.quantity)].map((_, i) => (
                      <CheckboxListItem
                        key={i}
                        title={orderItem.Product.name ?? ""}
                        onClick={() => {
                          const id = `${orderItem.Product.id}-${i}`;
                          if (selectedIds?.includes(id)) {
                            removeOrderItemAndDecreaseRefundAmount(
                              orderItem.price,
                              id,
                            );
                          } else {
                            addOrderItemAndIncreaseRefundAmount(
                              orderItem.price,
                              id,
                            );
                          }
                        }}
                        checked={
                          !!selectedIds?.includes(
                            `${orderItem.Product.id}-${i}`,
                          )
                        }
                        className={
                          selectedIds?.includes(`${orderItem.Product.id}-${i}`)
                            ? "bg-gray-100"
                            : undefined
                        }
                      />
                    ))}
                  </Fragment>
                ))}
              </div>
              <InputLabel>Refund amount</InputLabel>
              <InputContainer>
                <InputCurrency
                  showPrefix
                  allowNegative
                  {...form.register("refundAmount", {
                    required: true,
                    min: 0,
                    valueAsNumber: true,
                  })}
                />
              </InputContainer>
              <div className="flex flex-row gap-2">
                <ReactHookFormSwitch
                  {...form.register("includeVat", { required: false })}
                  control={form.control}
                />
                <p>Include VAT</p>
              </div>
              <InputLabel>Add a note</InputLabel>
              <InputContainer>
                <InputText
                  {...form.register("notes", {
                    required: true,
                  })}
                />
              </InputContainer>
              {!!refundAmount && (
                <Alert
                  className={"w-full"}
                  message={""}
                  title={`${buyerName} will be refunded ${formatCurrency(getRefundTotal(refundAmount, includeVat), currency)}`}
                  type={"information"}
                />
              )}
              <DialogCTA>
                <Button
                  type={"submit"}
                  loading={refundCreateMutation.isPending}
                >
                  Confirm
                </Button>
              </DialogCTA>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
